/**
 * Merged Filter Option Utility
 *
 * Generalizes the registrar merge pattern (`formatRegistrar.util.ts`) for any
 * filter surface whose raw backend values render duplicate display labels:
 * raw statistic values are grouped by their formatted label so the dropdown
 * lists each label exactly once, selections are expanded back to every raw
 * variant at apply time (the backend matches exactly and case-sensitively, so
 * the query must carry the raws, never the label), and stored raw values are
 * collapsed to labels for selection display.
 *
 * The merge key is the EXACT label-function output, byte-for-byte — there is
 * no additional normalization (trim/casefold/punctuation-strip) on top of the
 * label function. Raw variants merge exactly when the label function itself
 * folds them (e.g. `formatPrettyName` casefolds and splits on `[-_\s]`), so
 * "Palo Alto Networks, Inc" and "Palo Alto Networks, Inc." render distinct
 * labels and stay distinct options.
 *
 * Collapse/reconcile are OPTIONS-DRIVEN (reverse lookup through each option's
 * `rawValues` membership) rather than recomputing labels per raw: the label
 * function lives in the hook that builds the options, and recomputing it at
 * every consumer risks disagreeing with the canonical group value — any
 * mismatch makes `FilterDropdown` synthesize a stray chip for the unmatched
 * value (FilterDropdown.tsx:134-158). Options without `rawValues` make both
 * functions a pass-through no-op, so consumers can call them unconditionally
 * on mixed option sets (e.g. tags).
 *
 * Label-function contract: the label fn must be LOSSLESS enough that one
 * label never spans genuinely distinct values. `normalizeAsnameValue`
 * (statisticValidation.util.ts) is DISALLOWED as a merge-key label fn in its
 * current form — it truncates at the first space ("AMAZON-02 - Amazon.com,
 * Inc." → "AMAZON-02"), which would merge distinct AS names. Every future
 * surface onboarded onto this util requires a lossiness review of its label
 * function first.
 *
 * Current adopters: Seeds registrant/customer, Technology
 * vendor/product/version, and the registrar util (delegation). Intended
 * future adopters (follow-up tickets): Seeds country, Asset asname/country
 * (after an asname label-fn rework per the above), AD domain.
 */

/**
 * One merged filter option group: a canonical display label plus every raw
 * backend value whose label collapses to it. Mirrors
 * {@link RegistrarFilterGroup} (formatRegistrar.util.ts), which delegates here.
 */
export interface MergedFilterGroup {
  /** Canonical display label — used as the dropdown option value/id. First-seen label wins. */
  value: string;
  /** All raw backend values whose formatted label collapses to this group. */
  rawValues: string[];
}

/**
 * Upper bound on `rawValues` per merged group. Raw statistic values are
 * attacker-influenced (WHOIS/scan data), so a crafted data set could otherwise
 * balloon one group — and every stored filter expansion — without bound.
 * Overflow keeps the first-seen 100 raws and drops the tail from expansion:
 * the query under-matches the dropped tail, which is never worse than the
 * pre-merge behavior (where only ONE raw variant was ever queried). One
 * `console.warn` is emitted per overflowing group.
 */
export const MAX_RAW_VALUES_PER_GROUP = 100;

/**
 * Groups raw backend values by their formatted display label so a filter
 * dropdown lists each label exactly once. The merge key is `formatLabel(raw)`
 * exactly (identity keying — see the module docs); raws whose label is `null`
 * or `''` are dropped. First-seen order is preserved for groups and for the
 * raw values within each group; raws are de-duplicated per group and capped
 * at {@link MAX_RAW_VALUES_PER_GROUP}.
 *
 * @param rawValues - Raw values from the backend statistics
 * @param formatLabel - The surface's display-label function (must be
 *   lossless as a merge key — see the module docs)
 * @returns One {@link MergedFilterGroup} per distinct label
 */
export function buildMergedFilterGroups(
  rawValues: string[],
  formatLabel: (raw: string) => string | null
): MergedFilterGroup[] {
  const groups = new Map<string, string[]>();
  const overflowedLabels = new Set<string>();
  for (const raw of rawValues) {
    const label = formatLabel(raw);
    if (label == null || label === '') {
      continue;
    }
    const existing = groups.get(label);
    if (!existing) {
      groups.set(label, [raw]);
      continue;
    }
    if (existing.includes(raw)) {
      continue;
    }
    if (existing.length >= MAX_RAW_VALUES_PER_GROUP) {
      if (!overflowedLabels.has(label)) {
        overflowedLabels.add(label);
        console.warn(
          `buildMergedFilterGroups: group "${label}" exceeded ${MAX_RAW_VALUES_PER_GROUP} raw values; dropping overflow from expansion`
        );
      }
      continue;
    }
    existing.push(raw);
  }
  return Array.from(groups, ([value, raws]) => ({
    value,
    rawValues: raws,
  }));
}

/**
 * Expands selected canonical labels back to every raw backend value in their
 * group, so an exact-match query matches ALL raw variants — applied at
 * filter-apply time so the stored filter state is always the set of raw
 * values. A selected value with no matching option (or whose option carries
 * no `rawValues` — absent OR empty) passes through unchanged. De-duplicated.
 *
 * `expandRegistrarFilterValues` delegates here (identical algorithm).
 *
 * @param selectedValues - Selected dropdown option values (canonical labels)
 * @param options - The merged filter options, each carrying its `rawValues`
 * @returns The de-duplicated raw backend values to send to the query
 */
export function expandMergedFilterValues(
  selectedValues: string[],
  options: ReadonlyArray<{ value?: string | number; rawValues?: string[] }>
): string[] {
  const rawValuesByLabel = new Map<string, string[]>();
  for (const option of options) {
    // ?.length: an option with an EMPTY rawValues array must behave like one
    // with no rawValues at all (selection passes through unchanged) — a
    // zero-iteration expansion would silently drop the selection.
    if (option.value != null && option.rawValues?.length) {
      rawValuesByLabel.set(String(option.value), option.rawValues);
    }
  }

  const expanded = new Set<string>();
  for (const value of selectedValues) {
    const rawValues = rawValuesByLabel.get(value);
    if (rawValues) {
      for (const raw of rawValues) {
        expanded.add(raw);
      }
    } else {
      expanded.add(value);
    }
  }
  return Array.from(expanded);
}

/**
 * Collapses stored raw filter values to their canonical option values for
 * dropdown selection display — the inverse of {@link expandMergedFilterValues}.
 * A stored raw is matched by membership in an option's `rawValues` (first
 * matching option wins); an unmatched raw passes through unchanged
 * (stats-lag window: display-only degradation, never a query change).
 * De-duplicated, first-seen order. Options without `rawValues` make this a
 * full pass-through no-op — the guarantee consumers with mixed option sets
 * (e.g. tags) rely on.
 *
 * @param storedRawValues - Raw values currently stored in the filter state
 * @param options - The current merged filter options
 * @returns The de-duplicated canonical values to mark as selected
 */
export function collapseMergedFilterValues(
  storedRawValues: string[],
  options: ReadonlyArray<{ value?: string | number; rawValues?: string[] }>
): string[] {
  const labelByRaw = new Map<string, string>();
  for (const option of options) {
    if (option.value != null && option.rawValues) {
      for (const raw of option.rawValues) {
        if (!labelByRaw.has(raw)) {
          labelByRaw.set(raw, String(option.value));
        }
      }
    }
  }

  const collapsed = new Set<string>();
  for (const raw of storedRawValues) {
    collapsed.add(labelByRaw.get(raw) ?? raw);
  }
  return Array.from(collapsed);
}

/**
 * Reconciles stored raw filter values against the CURRENT merged options:
 * collapses the stored raws to canonical values, re-expands those values to
 * every raw variant currently present in `options`, and returns the UNION of
 * the original stored values and that expansion (stored first, then new
 * expansion values, de-duplicated).
 *
 * Superset guarantee: because the stored values are always retained, the
 * effective filter can only ever match MORE rows than the stored one, never
 * fewer — a stale stored raw is a harmless exact-match member, and newly
 * appeared raw variants are picked up without the user re-applying the
 * dropdown. With empty/pending options the result degrades gracefully to
 * just the stored values. Mirrors `reconcileRegistrarFilterValues`
 * (formatRegistrar.util.ts:253-262).
 *
 * @param storedRawValues - Raw values persisted in filter state (pre-fix
 *   state may hold a single raw variant)
 * @param options - The current merged filter options
 * @returns The de-duplicated raw values to send to the query (a superset of
 *   `storedRawValues`)
 */
export function reconcileMergedFilterValues(
  storedRawValues: string[],
  options: ReadonlyArray<{ value?: string | number; rawValues?: string[] }>
): string[] {
  const expanded = expandMergedFilterValues(
    collapseMergedFilterValues(storedRawValues, options),
    options
  );
  return Array.from(new Set([...storedRawValues, ...expanded]));
}
